import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { filter, take } from 'rxjs/operators';
import { PolicyholderService } from '@modules/policy/services/policy/policyholder.service';
import { PolicyDataService } from '@modules/policy/services/shared/policy-data.service';
import { FormComponent } from '@modules/policy/shared/components/form/form.component';
import { handleValidations } from '@modules/policy/shared/rules/handle-validations.rules';
import { ClientSearchOptions } from '@shared/models/client-search-options.model';
import PolicyholderTabs from '@shared/models/policyholder.model';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-policyholder',
  templateUrl: './policyholder.component.html',
  styleUrls: ['./policyholder.component.scss']
})
export class PolicyholderComponent implements OnInit {
  policyholderForm: FormGroup;
  driverSearchForm: FormGroup;
  nodeId: string;
  shouldHideClientField: boolean;
  policyId: number;
  cachedValues;
  private _clientKey;
  options: ClientSearchOptions;
  isAdditionalNamedInsured = false;

  set clientKey(newKey: string | null) {
    if (newKey) {
      this.policyholderForm?.disable();
      this.driverSearchForm?.disable();
      this.policyholderForm?.get('EnableDriverSearch')?.disable();
      this.policyholderForm?.get('LicenseDetails')?.get('DriverLicenceNumberTxt')?.disable();
      
    } else {
      this.driverSearchForm?.enable();
      //this.policyholderForm?.get('EnableDriverSearch')?.enable();
    }
    this._clientKey = newKey;
  }

  get clientKey() {
    return this._clientKey;
  }
  tabType: string;
  customerNumIsEmpty: boolean;
  addAsDriverEnabled: boolean = false;
  @ViewChild('formComponent', { static: false }) formComponent: FormComponent;

  constructor(
    private PolicyholderService: PolicyholderService,
    private route: ActivatedRoute,
    private PolicyDataService: PolicyDataService,
    private router: Router,
  ) { }

  ngOnInit() {
    const currentRoute = this.router.url.split("/");
    this.tabType = currentRoute[currentRoute.indexOf("policyholder-information") - 1];
    this.policyId = Number(this.PolicyDataService.getPolicyID());
    this.options = {
      sectionTitle: 'Contact Search',
      objectName: 'contact',
      clientReturnKeys: {
        clientKey: 'PolicyholderISIContactKey',
        contactReturnKey: 'SearchContactSearchCd',
        addressKey: 'SearchContactAddressPostalTxt',
        nameKey: 'SearchContactNameTxt',
      },
      formFields: {
        formName: 'Policyholder',
        firstNameField: 'PolicyholderFirstNameTxt',
        lastNameField: 'PolicyholderLastNameTxt',
        companyNameField: 'PolicyholderCompanyNameTxt',
        postalCodeField: 'PolicyholderAddressPostalTxt'
      },
      service: this.PolicyholderService
    }
  }

  saveBeforeDeactivate() {
    return this.formComponent.saveBeforeDeactivate();
  }

  showSaveSnackbar() {
    this.formComponent.showSaveSnackbar();
    this.formComponent.updateNavigationTree();
  }

  initForm(form: FormGroup) {
    this.nodeId = this.route.snapshot.params.formId;
    this.policyholderForm = form;
    this.assignFormName(form.controls as any);
    this.isAdditionalNamedInsured = this.checkIfAdditionalNamedInsured();
    this.appendFormToDataSource(form);
    this.setCachedFormValues();
    if (this.formComponent) {
      handleValidations(this.formComponent.formjson, this.policyholderForm);
    }
  }

  private checkIfAdditionalNamedInsured(): boolean {
    const currentNodeId = Number(this.nodeId);
    const states = this.PolicyDataService.stateSubject.value;
    const policyholders = states?.client?.policyholders || [];
    return policyholders.some(
      (policyholder: any) => policyholder?.policyholderInformation?.id === currentNodeId
    );
  }

  canCopyPrimaryClientAddress(): boolean {
    if (!this.isAdditionalNamedInsured || !this.policyholderForm || !!this.clientKey) {
      return false;
    }
    const infoForm = this.getPolicyholderInfoForm();
    if (!infoForm) {
      return false;
    }
    const odysseyField = infoForm.get('PolicyholderAddressSearchTxt');
    const odysseyValue = odysseyField?.value;
    return !(odysseyValue !== null && odysseyValue !== undefined && String(odysseyValue).trim() !== '');
  }

  copyAddressFromPrimaryClient(): void {
    if (!this.canCopyPrimaryClientAddress()) {
      return;
    }
    const states = this.PolicyDataService.stateSubject.value;
    const clientInfoNodeId = states?.client?.clientInformation?.id;
    if (!clientInfoNodeId) {
      return;
    }
    this.PolicyDataService.getData(this.policyId, clientInfoNodeId).pipe(take(1)).subscribe((res) => {
      const primaryData = res?.data || {};
      const targetForm = this.getPolicyholderInfoForm();
      if (!targetForm) {
        return;
      }
      const mappedAddressValues = {
        PolicyholderStreetAddressTxt: primaryData.ClientStreetAddressTxt,
        PolicyholderStreetDirectionCd: primaryData.ClientStreetDirectionCd,
        PolicyholderAddressUnitDesignationCd: primaryData.ClientAddressUnitDesignationCd,
        PolicyholderAddressUnitNum: primaryData.ClientAddressUnitNum,
        PolicyholderAddressPOBoxTxt: primaryData.ClientAddressPOBoxTxt,
        PolicyholderAddressRouteTypeCd: primaryData.ClientAddressRouteTypeCd,
        PolicyholderAddressRouteNum: primaryData.ClientAddressRouteNum,
        PolicyholderAddressStationNameCd: primaryData.ClientAddressStationNameCd,
        PolicyholderAddressStationNum: primaryData.ClientAddressStationNum,
        PolicyholderAddressCountryCd: primaryData.ClientAddressCountryCd,
        PolicyholderAddressProvinceCd: primaryData.ClientAddressProvinceCd,
        PolicyholderAddressMunicipalityTxt: primaryData.ClientAddressMunicipalityTxt,
        PolicyholderAddressPostalTxt: primaryData.ClientAddressPostalTxt
      };
      Object.entries(mappedAddressValues).forEach(([field, value]) => {
        const control = targetForm.get(field);
        if (control) {
          control.setValue(value ?? null);
        }
      });
      this.policyholderForm.markAsDirty();
    });
  }

  private getPolicyholderInfoForm(): FormGroup | null {
    const formName = this.options?.formFields?.formName;
    if (!formName || !this.policyholderForm?.controls?.[formName]) {
      return null;
    }
    return this.policyholderForm.controls[formName] as FormGroup;
  }

  assignFormName(forms: { [f: string]: FormGroup }) {
    const targetFields = Object.values(this.options.formFields);

    for (const [formName, formGroup] of Object.entries(forms)) {
      const formFields = Object.keys(formGroup.controls);
      const isContactForm = targetFields.some(field => formFields.includes(field));
      
      if (!isContactForm) continue;
      this.options.formFields.formName = formName;
      return;
    }
  }

  appendFormToDataSource(form: FormGroup) {
    this.PolicyholderService.dataSourceSubject.pipe(
      filter(data => {
        const node: PolicyholderTabs = data.find(e => e.id === Number(this.nodeId));
        return node && node.formGroup === undefined;
      })
    ).subscribe(data => {
      data.find(e => e.id === Number(this.nodeId)).formGroup = form;
      this.PolicyholderService.updateDataSource(data);
    });
  }

  searchFormCreated(searchForm: FormGroup) {
    this.driverSearchForm = searchForm;
    if (this.clientKey) {
      this.driverSearchForm.disable();
      this.policyholderForm?.get('EnableDriverSearch')?.disable();
    }
  }

  onCustomerNumChange(event: { value: any, isEmpty: boolean }) {
    this.customerNumIsEmpty = event.isEmpty;
  }

  onAddAsDriverChange(enabled: boolean) {
    this.addAsDriverEnabled = enabled;
  }

  driverFound(driverData: Object) {
    const formName = this.options.formFields.formName;
    const infoForm = this.policyholderForm.controls[formName] as FormGroup;
    for (const [fieldName, value] of Object.entries(driverData)) {
      if (!infoForm.contains(fieldName)) continue;
      infoForm.get(fieldName).setValue(value);
    }
    this.policyholderForm.markAsDirty();
  }


  setCachedFormValues() {
    this.cachedValues = this.PolicyDataService.getValueFromCache(this.nodeId);
    if (this.cachedValues && (this.cachedValues[this.options.clientReturnKeys.clientKey])) {
      this.clientKey = this.cachedValues[this.options.clientReturnKeys.clientKey];
    } else {
      this.clientKey = null;
    }
  }

  updateSubtabName(data) {
    const { PolicyholderDescriptionTxt } = data;
    this.PolicyholderService.dataSourceSubject.pipe(
      take(1)
    ).subscribe(data => {
      const nodeData = data.find(e => e.id === Number(this.nodeId));
      if (nodeData != null) {
        nodeData.$name = PolicyholderDescriptionTxt;
      }
      this.PolicyholderService.updateDataSource(data);
    });
  }

  setClientKey(newClientKey: string) {
    this.clientKey = newClientKey;
    this.PolicyDataService.getData(this.policyId, this.nodeId).subscribe((res) => {
      this.updateSubtabName(res.data);
    });
  }
}
